import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GitCompare, Printer, Zap, AlertTriangle, Sparkles } from "lucide-react";
import { Shell } from "@/components/Shell";
import { listBusinesses, getBusiness, getType } from "@/data/business";
import { getSections, formatAnswer } from "@/data/questionnaire";
import { AREAS } from "@/data/strategy";
import { compare, submissionsFor } from "@/data/compare";
import { formatDate } from "@/lib/storage";

const Picker = ({ side, bizId, subId, onBiz, onSub, businesses }) => {
  const subs = submissionsFor(bizId);
  return (
    <div className="card p-5 grid gap-3" data-testid={`compare-picker-${side}`}>
      <p className="eyebrow">Attività {side}</p>
      <select className="field" value={bizId} onChange={(e) => onBiz(e.target.value)} data-testid={`compare-biz-${side}`}>
        {businesses.map((b) => <option key={b.id} value={b.id}>{b.name || "Nuova attività (senza nome)"} · {getType(b.type).label}</option>)}
      </select>
      {subs.length === 0 ? (
        <p className="text-sm text-[#A83232]" data-testid={`compare-nosub-${side}`}>Nessuna compilazione per questa attività su questo tablet.</p>
      ) : (
        <select className="field" value={subId} onChange={(e) => onSub(e.target.value)} data-testid={`compare-sub-${side}`}>
          {subs.map((s) => <option key={s.id} value={s.id}>{s.respondent || "Senza nome"} · {formatDate(s.createdAt)}{s.status !== "completed" ? " (bozza)" : ""}</option>)}
        </select>
      )}
    </div>
  );
};

const Column = ({ side, biz, sub, strategy }) => {
  const sections = getSections(sub.businessType, biz);
  const q = (id) => sections.flatMap((s) => s.questions).find((x) => x.id === id);
  return (
    <div className="card p-6 grid gap-5" data-testid={`compare-col-${side}`}>
      <div>
        <p className="eyebrow">{getType(biz.type).label}</p>
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C221E] mt-1">{biz.name || "Senza nome"}</h2>
        <p className="text-sm text-[#7A6B63] mt-1">{sub.respondent || "Compilazione"} · {formatDate(sub.createdAt)}</p>
      </div>
      <div className="rounded-2xl p-4 bg-[#F1F4EC] border border-[#6E835855]">
        <p className="text-xs uppercase tracking-widest font-semibold text-[#8A7970]">Strategia suggerita</p>
        <p className="font-serif text-xl font-semibold text-[#2D4030] mt-1" data-testid={`compare-label-${side}`}>{strategy.label}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest font-semibold text-[#8A7970] mb-2">Azioni primarie</p>
        {strategy.primary.length === 0 ? <p className="text-sm italic text-[#B0A096]">Servono più risposte</p> : (
          <ol className="grid gap-2">
            {strategy.primary.map((a, i) => (
              <li key={a.id} className="flex gap-2 text-sm"><span className="font-serif font-semibold text-[#C85A32]">{i + 1}</span><span><span className="font-semibold text-[#2C221E]">{a.title}</span> <span className="text-[#8A7970]">· {AREAS[a.area]}</span></span></li>
            ))}
          </ol>
        )}
      </div>
      <dl className="grid gap-2 text-sm">
        {[["q19", "Riempimento"], ["q20", "Prezzi vs concorrenti"], ["q21", "Margine conosciuto"], ["q23", "Senza i titolari"], ["q16", "Prima priorità"], ["q25", "Voglia di cambiare"]].map(([id, label]) => (
          <div key={id} className="flex flex-col sm:flex-row sm:gap-3 border-t border-[#EADFD2] pt-2">
            <dt className="text-[#8A7970] sm:w-40 flex-none">{label}</dt>
            <dd className="text-[#2C221E]">{(q(id) && formatAnswer(q(id), sub.answers[id])) || <span className="italic text-[#B0A096]">non risposto</span>}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const Box = ({ icon: Icon, title, sub, color, bg, items, empty, testId }) => (
  <div className="rounded-2xl p-5 border" style={{ background: bg, borderColor: `${color}55` }} data-testid={testId}>
    <div className="flex items-center gap-2"><Icon size={18} style={{ color }} /><p className="font-serif text-xl font-semibold" style={{ color }}>{title}</p></div>
    <p className="text-xs uppercase tracking-widest font-semibold text-[#8A7970] mt-0.5 mb-3">{sub}</p>
    {items.length === 0 ? <p className="text-sm italic text-[#B0A096]">{empty}</p> : <ul className="grid gap-2">{items.map((t, i) => <li key={i} className="text-sm text-[#2C221E] flex gap-2"><span style={{ color }}>•</span><span>{t}</span></li>)}</ul>}
  </div>
);

export default function ComparePage() {
  const navigate = useNavigate();
  const businesses = useMemo(listBusinesses, []);
  const firstSub = (id) => submissionsFor(id)[0]?.id || "";
  const [bizA, setBizA] = useState(getBusiness().id);
  const [bizB, setBizB] = useState(() => (businesses.find((b) => b.id !== getBusiness().id) || businesses[0]).id);
  const [subA, setSubA] = useState(() => firstSub(getBusiness().id));
  const [subB, setSubB] = useState(() => firstSub((businesses.find((b) => b.id !== getBusiness().id) || businesses[0]).id));

  const pickBiz = (setBiz, setSub) => (id) => { setBiz(id); setSub(firstSub(id)); };
  const A = { biz: businesses.find((b) => b.id === bizA), sub: submissionsFor(bizA).find((s) => s.id === subA) };
  const B = { biz: businesses.find((b) => b.id === bizB), sub: submissionsFor(bizB).find((s) => s.id === subB) };
  const ready = A.sub && B.sub;
  const result = ready ? compare(A, B) : null;

  return (
    <Shell header={<button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate("/")} data-testid="compare-back-btn"><ArrowLeft size={16} /> Elenco</button>}>
      <div className="fade-up">
        <div className="flex items-center gap-3"><GitCompare className="text-[#2D4030]" size={24} /><p className="eyebrow">Confronto</p></div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2">Due attività a confronto</h1>
        <p className="text-[#5C4D46] mt-3 max-w-xl">Scegli due clienti e le loro compilazioni: vedi affiancate strategia, priorità e risposte chiave, con i punti deboli in comune e le cose che si possono sistemare in fretta.</p>
        {businesses.length < 2 && <p className="text-sm text-[#A64B2A] mt-3" data-testid="compare-one-business">Hai una sola attività: puoi comunque confrontare due compilazioni diverse della stessa attività, oppure aggiungerne una da “Attività”.</p>}

        <div className="grid md:grid-cols-2 gap-4 mt-8 no-print">
          <Picker side="A" bizId={bizA} subId={subA} onBiz={pickBiz(setBizA, setSubA)} onSub={setSubA} businesses={businesses} />
          <Picker side="B" bizId={bizB} subId={subB} onBiz={pickBiz(setBizB, setSubB)} onSub={setSubB} businesses={businesses} />
        </div>

        {ready && (
          <>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <Column side="A" biz={A.biz} sub={A.sub} strategy={result.sa} />
              <Column side="B" biz={B.biz} sub={B.sub} strategy={result.sb} />
            </div>
            <section className="card p-6 sm:p-9 mt-6" data-testid="compare-insights">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="eyebrow">Cosa emerge dal confronto</p>
                <button className="btn btn-ghost !min-h-[44px] text-sm no-print" onClick={() => window.print()} data-testid="compare-print-btn"><Printer size={16} /> Stampa</button>
              </div>
              <div className="grid md:grid-cols-3 gap-5 mt-6">
                <Box icon={AlertTriangle} title="Punti deboli comuni" sub="Dove entrambe faticano" color="#C85A32" bg="#FBF1EA" testId="compare-common"
                  items={[...result.commonActions.map((a) => a.title), ...result.sharedIssues.map((s) => `Intoppo condiviso: ${s.toLowerCase()}`)]} empty="Nessun punto debole in comune rilevante" />
                <Box icon={Zap} title="Opportunità rapide" sub="Risolvibili in poche settimane" color="#B07A12" bg="#FDF6E6" testId="compare-quickwins"
                  items={result.quickWins} empty="Nessuna scorciatoia comune: servono interventi su misura" />
                <Box icon={Sparkles} title="Differenze da notare" sub="Priorità che valgono per una sola" color="#6E8358" bg="#F1F4EC" testId="compare-differences"
                  items={[...result.onlyA.map((a) => `Solo ${A.biz.name || "A"}: ${a.title}`), ...result.onlyB.map((a) => `Solo ${B.biz.name || "B"}: ${a.title}`)]} empty="Le due attività hanno le stesse priorità" />
              </div>
            </section>
          </>
        )}
      </div>
    </Shell>
  );
}
